// components/DocumentModal.jsx

import React from 'react';
import { Modal, Card, Collapse } from 'antd';
import { FileSearchOutlined, CloseOutlined } from '@ant-design/icons';
import { isEmpty, processMktuClasses, renderDocumentFieldValue } from '../../../utils/helpers';

const { Panel } = Collapse;

/**
 * DocumentModal Component
 *
 * @param {Object} props - Компонентные пропсы.
 * @param {boolean} props.open - Видимость модального окна.
 * @param {Function} props.onClose - Функция закрытия модального окна.
 * @param {Object|null} props.doc - Объект документа с данными или null.
 * @param {string|null} props.imgSrc - Источник изображения в формате Base64 или null.
 * @returns {JSX.Element|null}
 */
const DocumentModal = ({ open, onClose, doc, imgSrc }) => {
  // Проверка на истекший срок действия
  if (!doc) return null;

  const rawDocument = doc.document || doc;
  const score = typeof doc.score !== 'undefined' ? doc.score : rawDocument?.score;
  const document = rawDocument;

  // Словарь меток для полей (старые и новые)
  const fieldLabels = {
    score: 'Коэффициент похожести',
    '190_country': 'Страна',
    '111_numReg': 'Номер регистрации',
    url_numReg: 'Ссылка на Документ',
    StatusText: 'Статус',
    Category: 'Категория',
    '210_numZayvki': 'Номер заявки',
    '181_dataIstIsklPrava': 'Дата истечения исключительного права',
    url_210_numZayvki: 'Ссылка на заявку',
    Prioritet: 'Приоритет',
    '220_dataPodachyZayav': 'Дата подачи заявки',
    '151_dataGosRegistr': 'Дата государственной регистрации',
    '450_dataPublic': 'Дата публикации',
    url_450_dataPublic: 'Ссылка на публикацию',
    '732_provoobladatel': 'Правообладатель',
    '750_AdressDlyaPerepiski': 'Адрес для переписки',
    '511_KlassyMKTU': 'Классы МКТУ',
    '580_DataVnesenyaVGosReestr': 'Дата внесения в государственный реестр',
    '186_dataDoKotoroyProdlenSrokIsklPrava': 'Дата продления срока исключительного права',
    '526_neohranElem': 'Неохраняемые элементы',
    '591_ukazanieCveta': 'Указание цвета',
    '141_dataPrekrTrudOhr': 'Дата прекращения правовой охраны',
    '793_usloviaDogovora': 'Условия договора',
    realName: 'Название',
    // Новые поля
    img_url: 'Ссылка на изображение',
    '551_kollektivnyyЗнак': 'Коллективный знак',
    '554_obemnyyЗнак': 'Объемный знак',
    '555_gолографicheskiyЗнак': 'Голографический знак',
    '556_zvukовoyЗнак': 'Звуковой знак',
    '557_obonyatelnyyЗнак': 'Обонятельный знак',
    '641_yuridicheskiSvyazannyeZayavki': 'Юридически связанные заявки',
    '646_yuridicheskiSvyazannyeRegistracii': 'Юридически связанные регистрации',
    '550_vidznaka': 'Вид знака',
  };

  /**
   * Обёртка для рендера Descriptions.Item.
   * Если значение пустое, элемент не рендерится.
   */
  const renderFieldItem = (key, value) => {
    if (isEmpty(value)) return null;
    return (
      <div style={{ marginBottom: 8, padding: '4px 0' }}>
        <b className="theme-detail-field-label">{fieldLabels[key]}:</b> {renderDocumentFieldValue(key, value)}
      </div>
    );
  };

  const mktuInfo = processMktuClasses(document?.['511_KlassyMKTU']);

  // AI-данные (elements, colors, text_content, semantic_description)
  const aiData = rawDocument || doc;
  const title =
    document?.realName ||
    document?.Trademark ||
    document?.['MARKGR/CURRENT/IMAGE@TEXT'] ||
    document?.['MARKGR/CURRENT/REPGR/NAME'] ||
    document?.['@INTREGN'] ||
    'Без названия';
  const hasAiSection = aiData && (
    (Array.isArray(aiData.elements) && aiData.elements.length > 0) ||
    (Array.isArray(aiData.colors) && aiData.colors.length > 0) ||
    (Array.isArray(aiData.text_content) && aiData.text_content.length > 0) ||
    (typeof aiData.semantic_description === 'string' && aiData.semantic_description.trim() !== '')
  );

  return (
    <Modal
      open={open}
      footer={null}
      onCancel={onClose}
      centered
      destroyOnClose
      width="min(960px, 92vw)"
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', width: '100%' }}
      closeIcon={<CloseOutlined style={{ fontSize: '20px', color: '#000' }} />} // Настройка иконки закрытия (опционально)
    >
      <Card
        title={
          <div className="theme-modal-title">
            {title}
          </div>
        }
        cover={
          imgSrc ? (
            <div style={{ textAlign: 'center', padding: '20px 0', maxWidth: '100%', boxSizing: 'border-box' }}>
              <img
                src={imgSrc}
                alt={title}
                style={{
                  display: 'block',
                  margin: '0 auto',
                  maxWidth: '100%',
                  width: 'auto',
                  height: 'auto',
                  maxHeight: 'min(72vh, 85vw)',
                  borderRadius: 8,
                  objectFit: 'contain',
                }}
              />
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <FileSearchOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            </div>
          )
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Основная информация */}
          <div className="theme-detail-section">
            <h4 className="theme-detail-section__title">📋 Основная информация</h4>
          {renderFieldItem('score', score)}
            {renderFieldItem('StatusText', document?.StatusText)}
            {renderFieldItem('Category', document?.Category)}
          {renderFieldItem('190_country', document?.['190_country'])}
          {renderFieldItem('111_numReg', document?.['111_numReg'])}
          {renderFieldItem('210_numZayvki', document?.['210_numZayvki'])}
            {renderFieldItem('732_provoobladatel', document?.['732_provoobladatel'])}
            {renderFieldItem('750_AdressDlyaPerepiski', document?.['750_AdressDlyaPerepiski'])}
          </div>

          {/* Даты */}
          <div className="theme-detail-section">
            <h4 className="theme-detail-section__title">📅 Даты</h4>
          {renderFieldItem('220_dataPodachyZayav', document?.['220_dataPodachyZayav'])}
          {renderFieldItem('151_dataGosRegistr', document?.['151_dataGosRegistr'])}
          {renderFieldItem('450_dataPublic', document?.['450_dataPublic'])}
            {renderFieldItem('580_DataVnesenyaVGosReestr', document?.['580_DataVnesenyaVGosReestr'])}
            {renderFieldItem('181_dataIstIsklPrava', document?.['181_dataIstIsklPrava'])}
            {renderFieldItem('186_dataDoKotoroyProdlenSrokIsklPrava', document?.['186_dataDoKotoroyProdlenSrokIsklPrava'])}
            {renderFieldItem('141_dataPrekrTrudOhr', document?.['141_dataPrekrTrudOhr'])}
            {renderFieldItem('Prioritet', document?.Prioritet)}
          </div>

          {/* Ссылки */}
          <div className="theme-detail-section">
            <h4 className="theme-detail-section__title">🔗 Ссылки</h4>
            {renderFieldItem('url_numReg', document?.url_numReg)}
            {renderFieldItem('url_210_numZayvki', document?.url_210_numZayvki)}
          {renderFieldItem('url_450_dataPublic', document?.url_450_dataPublic)}
            {renderFieldItem('img_url', document?.img_url)}
          </div>

          {/* Классы МКТУ */}
          {!isEmpty(document?.['511_KlassyMKTU']) && (
            <div className="theme-detail-section">
              <h4 className="theme-detail-section__title">🏷️ Классы МКТУ</h4>
              <div style={{ marginBottom: 8, padding: '4px 0' }}>
                <b className="theme-detail-field-label">Классы:</b> {mktuInfo.numbers.join(', ')}
              </div>
              <Collapse style={{ maxHeight: '200px', overflowY: 'auto' }}>
                <Panel header="Показать полную расшифровку" key="1">
                  {renderDocumentFieldValue('511_KlassyMKTU', document?.['511_KlassyMKTU'])}
                </Panel>
              </Collapse>
            </div>
          )}

          {/* Специальные характеристики */}
          <div className="theme-detail-section">
            <h4 className="theme-detail-section__title">⚙️ Характеристики</h4>
          {renderFieldItem('526_neohranElem', document?.['526_neohranElem'])}
          {renderFieldItem('591_ukazanieCveta', document?.['591_ukazanieCveta'])}
          {renderFieldItem('793_usloviaDogovora', document?.['793_usloviaDogovora'])}
          {renderFieldItem('551_kollektivnyyЗнак', document?.['551_kollektivnyyЗнак'])}
          {renderFieldItem('554_obemnyyЗнак', document?.['554_obemnyyЗнак'])}
          {renderFieldItem('555_gолографicheskiyЗнак', document?.['555_gолографicheskiyЗнак'])}
          {renderFieldItem('556_zvukовoyЗнак', document?.['556_zvukовoyЗнак'])}
          {renderFieldItem('557_obonyatelnyyЗнак', document?.['557_obonyatelnyyЗнак'])}
            {renderFieldItem('550_vidznaka', document?.['550_vidznaka'])}
          </div>

          {/* Связанные документы */}
          <div className="theme-detail-section">
            <h4 className="theme-detail-section__title">📄 Связанные документы</h4>
          {renderFieldItem('641_yuridicheskiSvyazannyeZayavki', document?.['641_yuridicheskiSvyazannyeZayavki'])}
          {renderFieldItem('646_yuridicheskiSvyazannyeRegistracii', document?.['646_yuridicheskiSvyazannyeRegistracii'])}
          </div>

          {/* AI */}
          {hasAiSection && (
            <div className="theme-detail-section">
              <h4 className="theme-detail-section__title">🤖 AI</h4>
              {Array.isArray(aiData.elements) && aiData.elements.length > 0 && (
                <div style={{ marginBottom: 8, padding: '4px 0' }}>
                  <b className="theme-detail-field-label">Элементы (ИИ):</b> {renderDocumentFieldValue('', aiData.elements)}
                </div>
              )}
              {Array.isArray(aiData.colors) && aiData.colors.length > 0 && (
                <div style={{ marginBottom: 8, padding: '4px 0' }}>
                  <b className="theme-detail-field-label">Цвета (ИИ):</b> {renderDocumentFieldValue('', aiData.colors)}
                </div>
              )}
              {Array.isArray(aiData.text_content) && aiData.text_content.length > 0 && (
                <div style={{ marginBottom: 8, padding: '4px 0' }}>
                  <b className="theme-detail-field-label">Текстовое содержание (ИИ):</b> {renderDocumentFieldValue('', aiData.text_content)}
                </div>
              )}
              {typeof aiData.semantic_description === 'string' && aiData.semantic_description.trim() !== '' && (
                <div style={{ marginBottom: 8, padding: '4px 0' }}>
                  <b className="theme-detail-field-label">Семантическое описание (ИИ):</b> {aiData.semantic_description.trim()}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </Modal>
  );
};

export default DocumentModal;
